import React, { useRef, useState } from 'react';
import { useLocale } from 'locale';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import * as Styled from './styled';
import { CommonWrapper, SectionTitle } from 'screens/MortgageOfficePage/styled';
import { VideoSource } from 'utils/entities';
import config from 'config';
import { PageDetails } from 'screens/AgentOfficePages/types';
import withEvents from 'analytics/withEvents';
import { noop } from 'lodash';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';
import { getAgentOfficeCategory } from 'screens/AgentOfficePages/helper';


interface VideoProps {
  videoUrl: string;
  videoSource: VideoSource;
  pageDetails: PageDetails;
  onVideoClickEvent?: (event: string, category: string, pageDetails: PageDetails) => void;
  hideTitle?: boolean;
}

const VideoBase: React.FC<VideoProps> = ({ videoUrl, videoSource, pageDetails, onVideoClickEvent = noop, hideTitle }) => {
  const { t } = useLocale();
  const el = useRef<HTMLDivElement>(null);
  const [ visible, setVisible ] = useState(false);
  const [ loaded, setLoaded ] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isS3 = videoSource.toLocaleUpperCase() === VideoSource.S3;
  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;
  const event = `${getAgentOfficeCategory(isOfficePage)}_video_click`;
  const category = getAgentOfficeCategory(isOfficePage);

  const handleVideoClick = () => {
    onVideoClickEvent(event, category, pageDetails);
  };

  useIntersectionObserver(([ e ]) => {
    if (e.isIntersecting) {
      setVisible(true);
    }
  }, [ el ], { threshold: 0 });

  if (!videoUrl) return null;

  const videoSection = isS3 ? (
    <video ref={videoRef} width="100%" height="auto" controls onPlay={handleVideoClick}>
      <source src={`${config.imagesRoot}${videoUrl}`} type="video/mp4" />
    </video>
  ) : (
    <iframe
      onClick={handleVideoClick}
      onLoad={() => setLoaded(true)}
      src={videoUrl}
      title={t('developerPage.promotionVideo.videoTitle')}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );

  return (
    <CommonWrapper>
      {hideTitle ? null : <SectionTitle>{t('developerPage.promotionVideo.title')}</SectionTitle>}
      <Styled.VideoWrapper>
        <Styled.VideoInner ref={el} skipPadding={isS3}>
          {visible ? videoSection : null}
          {loaded || isS3 ? null : (
            <Styled.VideoPlaceholder>
              <Styled.VideoPlaceholderBg />
            </Styled.VideoPlaceholder>
          )}
        </Styled.VideoInner>
      </Styled.VideoWrapper>
    </CommonWrapper>
  );
};

export const Video = withEvents<VideoProps>((sendEvent) => ({
  onVideoClickEvent: (event: string, category: string, pageDetails: PageDetails) => {
    sendEvent(event, category, {
      ...pageDetails,
    });
  },
}))(VideoBase);
